<template>
  <li
    class="console-action"
    :class="{ selected, gated: !!gate }"
    @click="onClick"
    @mouseover="onMouseover"
  >
    <div class="action-description">
      <div class="first-line">
        <img v-if="iconImage" :src="iconImage" class="icon-url" />
        <BaseTypeIcon v-else :image="icon" v-bind="iconProps" />
        <span class="text action-text">{{ actionText }}</span>
        <span v-if="titleDisplay" class="title action-text">{{
          titleDisplay
        }}</span>

        <template v-if="date">
          <span class="action action-text">{{ dateKeyword }}</span>
          <span class="info action-text">{{ date }}</span>
        </template>

        <template v-if="duration">
          <span class="action action-text">for</span>
          <span class="info action-text">{{ duration }}</span>
        </template>

        <template v-if="daytime">
          <span class="action action-text">at</span>
          <span class="info action-text">{{ daytime }}</span>
        </template>

        <template v-if="recurrence">
          <span class="action action-text">every</span>
          <span class="info action-text">{{ recurrence }}</span>
        </template>

        <template v-if="starting">
          <span class="action action-text">starting</span>
          <span class="info action-text">{{ starting }}</span>
        </template>

        <template v-if="ending">
          <span class="action action-text">until</span>
          <span class="info action-text">{{ ending }}</span>
        </template>
        <template v-if="allday">
          <span class="action action-text">all day</span>
        </template>

        <template v-if="holder">
          <span class="action action-text">in</span>
          <CalendarColorBar
            v-if="calendarColorBar"
            :color="calendarColorBar"
            class="calendar-bar"
          />
          <BaseTypeIcon
            v-else-if="holderTypeIcon"
            :image="holderTypeIcon"
            size="s"
            class="holder-type-icon"
          />
          <span class="info action-text">{{ holder }}</span>
        </template>

        <template v-if="nOccurrences">
          <span class="info action-text">({{ nOccurrences }} times)</span>
        </template>

        <template v-if="participants">
          <span class="action action-text">with</span>
          <span class="info action-text">{{ participants }}</span>
        </template>

        <template v-if="labelsDisplay">
          <span
            v-for="(label, idx) in labelsDisplay"
            :key="idx"
            class="label-item"
          >
            <span class="label-dot" :class="[`option-color-${label.color}`]" />
            <span class="label action-text">{{ label.name }}</span>
          </span>
        </template>

        <template v-if="priorityDisplay">
          <span
            v-if="priorityColor"
            class="priority-mark"
            :class="[`option-color-${priorityColor}`]"
            >!</span
          >
          <span class="info action-text">{{ priorityDisplay }}</span>
        </template>

        <template v-if="allocation">
          <span class="action action-text">as soon as possible</span>
        </template>
      </div>
      <div v-if="filteredContext" class="second-line">
        <BaseContextIcon :context="contextIcon" class="context-icon" />
        <span class="context-text">{{ contextText }}</span>
      </div>
    </div>
    <!-- A gated capture action can't be performed yet: submitting it opens a
         modal instead. A missing entitlement is locked (upgrade), while a
         disabled plugin shows the plugin icon (it can simply be enabled).
         The cross dismisses the advertisement for good: gated capture
         actions are then hidden from the console (see console/capture.ts). -->
    <div v-if="gate" class="gate">
      <LockIcon v-if="gate === 'upgrade'" class="gate-icon" />
      <PluginIcon v-else class="gate-icon" />
      <CloseIcon
        v-tippy="{
          content: 'Don\'t show again',
          arrow: false,
          placement: 'top',
        }"
        class="gate-dismiss"
        @click="onDismiss"
      />
    </div>
    <!-- Advertise the submit shortcuts: plain Enter submits, Mod+Enter
         submits and opens the created item. -->
    <div v-else class="submit-hints" :class="{ selected }">
      <span class="hint">
        <span class="keys">{{ enterKeysDisplay }}</span>
        <span class="hint-label">submit</span>
      </span>
      <span class="hint">
        <span class="keys">{{ modEnterKeysDisplay }}</span>
        <span class="hint-label">open</span>
      </span>
    </div>
  </li>
</template>

<script lang="ts">
import { formatDuration } from "date-fns";
import * as _ from "lodash-es";
import { match, Pattern } from "ts-pattern";
import {
  Component,
  computed,
  ComputedRef,
  defineComponent,
  PropType,
  toRefs,
} from "vue";

import controller from "@ctrl/controller";
import type {
  Daytime,
  PeopleId,
  ReferenceId,
  RoutineMandateLabel,
  Task,
} from "@ctrl/controller-rpc";

import BaseContextIcon, {
  IconContext,
} from "@/components/base/BaseContextIcon.vue";
import BaseTypeIcon from "@/components/base/BaseTypeIcon.vue";
import { getColor, resolveLabelColor } from "@/components/color";
import {
  captureGate,
  useCaptureAvailability,
} from "@/components/console/capture";
import { getIcon } from "@/components/context";
import { getTypeIcon } from "@/components/databases";
import { formatDate, isInTheWeek } from "@/components/dates";
import CalendarColorBar from "@/components/elements/CalendarColorBar.vue";
import { getObjectTitle } from "@/components/graph/title";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import EventIcon from "@/components/icons/EventIcon.vue";
import LockIcon from "@/components/icons/LockIcon.vue";
import MoveToIcon from "@/components/icons/MoveToIcon.vue";
import PageIcon from "@/components/icons/PageIcon.vue";
import PluginIcon from "@/components/icons/PluginIcon.vue";
import WorkspaceIcon from "@/components/icons/WorkspaceIcon.vue";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { getWorkspace } from "@/composables/useProfile";
import { referencePresentation } from "@/references/presentation";
import { resolveReferenceTitle } from "@/references/resolve";
import { ConsoleAction, Context } from "@/types";
import { captureContextDisplayText } from "@/utils/context";
import { hotkeys } from "@/utils/hotkeys";
import { keymap } from "@/utils/keymap";

import PersonIcon from "./icons/PersonIcon.vue";
import TaskIcon from "./icons/TaskIcon.vue";
import * as inject from "./inject";
import { Recurrence } from "./recurrence";

const {
  mandateCompletionStopper,
  nextOccurrences,
  occurrenceCount,
  ppRecurrence,
} = controller;

const formatMandateDuration = (duration: number | Daytime | null): string =>
  formatDuration(
    duration === null
      ? {}
      : typeof duration === "number"
        ? {
            hours: Math.floor(duration / 3600),
            minutes: (duration % 3600) / 60,
          }
        : {
            hours: duration.hours,
            minutes: duration.minutes,
          }
  );

export default defineComponent({
  components: {
    BaseTypeIcon,
    BaseContextIcon,
    CalendarColorBar,
    CloseIcon,
    LockIcon,
    PluginIcon,
  },
  props: {
    action: {
      type: Object as PropType<ConsoleAction>,
      required: true,
    },
    context: { type: Object as PropType<Context | null>, default: null },
    selected: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: false,
    },
    labels: {
      type: Array as PropType<RoutineMandateLabel[]>,
      default: () => [],
    },
    priority: {
      type: String,
      default: null,
    },
    task: {
      type: Object as PropType<Task>,
      default: null,
    },
  },
  emits: {
    click: (_payload: MouseEvent) => true,
    // The cross on a gated capture action: the user doesn't want to see the
    // capture advertisement anymore. The list owner persists the choice.
    dismiss: () => true,
    mouseover: (_payload: MouseEvent) => true,
  },
  async setup(props, { emit }) {
    const clock = inject.clock();
    const locale = inject.locale();
    const controller = inject.controller();
    const billing = inject.billing();
    const isContextualCaptureEnabled = inject.isContextualCaptureEnabled();
    const profile = inject.profile();
    const browserStore = useBrowserStore(profile.value.user.accountId);

    const captureAvailability = useCaptureAvailability(
      billing,
      isContextualCaptureEnabled
    );
    const { action, context, labels, priority, task, title } = toRefs(props);
    // Non-null when this is a capture action the user can't perform yet
    // (missing entitlement or disabled plugin); rendered gated.
    const gate = computed(() =>
      captureGate(action.value, captureAvailability.value)
    );
    // This component rely heavily on typescript pattern matching to be sure we handle every case
    // Find the doc here: https://github.com/gvergnaud/ts-pattern#ts-pattern
    const icon = computed<Component | string | undefined>(() => {
      return match(action.value)
        .with(
          { type: "event-allocation" },
          { type: "event-creation" },
          () => EventIcon
        )
        .with({ type: "page-creation" }, () => PageIcon)
        .with(
          { type: "task" },
          { type: "task-allocation" },
          { type: "task-postponing" },
          { type: "task-recurrence" },
          { type: "task-scheduling" },
          () => TaskIcon
        )
        .with({ type: "task-move" }, () => MoveToIcon)
        .with({ type: "recurrent-task-move" }, () => MoveToIcon)
        .with({ type: "custom-type" }, ({ database }) =>
          getTypeIcon(profile.value, database)
        )
        .exhaustive();
    });
    const iconProps = computed(() => {
      return match(action.value)
        .with(
          { type: "task" },
          { type: "task-allocation" },
          { type: "task-postponing" },
          { type: "task-scheduling" },
          () => {
            return {
              size: "s" as const,
            };
          }
        )
        .with({ type: "task-recurrence" }, () => {
          return {
            recurring: Recurrence.Recurring,
            size: "s" as const,
          };
        })
        .with({ type: "event-creation" }, () => {
          return {
            recurring: Recurrence.Recurring,
          };
        })
        .with(
          { type: "event-allocation" },
          { type: "page-creation" },
          { type: "task-move" },
          { type: "recurrent-task-move" },
          { type: "custom-type" },
          () => null
        )
        .exhaustive();
    });
    const iconImage = computed<string | null>(() =>
      match(action.value)
        .with({ type: "custom-type" }, ({ database }) => {
          const icon = database.meta?.icon?.trim();
          return icon && icon.length > 0 ? icon : null;
        })
        .otherwise(() => null)
    );
    const titleDisplay = computed<string | undefined>(() =>
      title.value
        ? match(action.value)
            .with(
              { type: "event-allocation" },
              { type: "event-creation" },
              () => title.value
            )
            .with(
              { type: "task" },
              { type: "task-allocation" },
              { type: "task-postponing" },
              { type: "task-scheduling" },
              { type: "custom-type" },
              () => title.value
            )
            .with({ type: "page-creation" }, () => title.value)
            .with(
              { type: "task-move" },
              { type: "recurrent-task-move" },
              { type: "task-recurrence" },
              () => undefined
            )
            .exhaustive()
        : undefined
    );
    const actionText = computed<string>(() => {
      return match(action.value)
        .with(
          { type: "event-allocation" },
          { type: "event-creation" },
          () => "Create event"
        )
        .with({ type: "page-creation" }, () => "Create page")
        .with({ type: "task" }, ({ capture }) =>
          capture ? "Capture task" : "Create task"
        )
        .with({ type: "task-allocation" }, ({ capture }) => {
          return task.value ? "Plan" : capture ? "Capture task" : "Create task";
        })
        .with({ type: "task-postponing" }, ({ capture }) =>
          task.value ? "Plan" : capture ? "Capture" : "Create"
        )
        .with({ type: "task-recurrence" }, () => "Create recurring task")
        .with({ type: "task-scheduling" }, ({ capture }) => {
          if (task.value) {
            if (task.value.scheduled && !task.value.allocations) {
              return "Replan";
            }
            return "Plan";
          }
          return capture ? "Capture" : "Create";
        })
        .with({ type: "task-move" }, () => "Move")
        .with({ type: "recurrent-task-move" }, () => "Move")
        .with({ type: "custom-type" }, ({ database, context: withContext }) => {
          const verb = withContext ? "Capture" : "Create";
          return `${verb} ${database.data_name.toLowerCase()}`;
        })
        .exhaustive();
    });
    const dateKeyword = computed(() => {
      return match(action.value)
        .with({ type: "task-postponing" }, () => "for")
        .otherwise(() => "for");
    });
    const date = computed(() => {
      return match(action.value)
        .with(
          {
            type: "event-allocation",
            dates: { type: "date" },
          },
          ({ dates }) => formatDate(dates.date)
        )
        .with(
          {
            dates: {
              type: "date_range",
              start: Pattern.select("start"),
              end: Pattern.select("end"),
            },
          },
          ({ start, end }) => locale.value.formatDateRange(start, end)
        )
        .with({ dates: { type: "week", week: Pattern.select() } }, (week) => {
          return locale.value.formatWeek(week);
        })
        .with(
          {
            dates: {
              type: "month",
              month: Pattern.select("month"),
              year: Pattern.select("year"),
            },
          },
          ({ month, year }) => `${_.capitalize(month)} ${year}`
        )
        .with(
          { dates: { type: "year", year: Pattern.select("year") } },
          ({ year }) => `${year}`
        )
        .with({ type: "task-scheduling" }, ({ date }) => formatDate(date))
        .with({ type: "task-postponing" }, ({ batch }) => {
          return isInTheWeek(clock.value, batch)
            ? "this week"
            : locale.value.formatWeek(batch);
        })
        .with(
          { type: "event-allocation" },
          { type: "event-creation" },
          { type: "page-creation" },
          { type: "task" },
          { type: "task-allocation" },
          { type: "task-move" },
          { type: "recurrent-task-move" },
          { type: "task-recurrence" },
          { type: "custom-type" },
          () => null
        )
        .exhaustive();
    });
    const duration = computed<string | null>(() => {
      return match(action.value)
        .with({ duration: Pattern.not(Pattern.nullish) }, ({ duration }) => {
          return formatDuration({
            hours: Math.floor(duration / 3600),
            minutes: (duration % 3600) / 60,
          });
        })
        .with(
          { daytime: Pattern.not(Pattern.nullish) },
          ({ daytime: [_, duration] }) => {
            return formatMandateDuration(duration);
          }
        )
        .otherwise(() => null);
    });
    const daytime = computed(() => {
      const daytimeToString = ({ hours, minutes }: Daytime) =>
        `${hours}:${minutes || "00"}`;
      return match(action.value)
        .with(
          { type: "event-creation", daytime: Pattern.not(Pattern.nullish) },
          ({ daytime: [start, _] }) => {
            return `${daytimeToString(start)}`;
          }
        )
        .with(
          { type: "event-allocation", daytime: Pattern.not(Pattern.nullish) },
          { type: "task-allocation", daytime: Pattern.not(Pattern.nullish) },
          ({ daytime }) => {
            const end =
              daytime.length == 2 && daytime[1]
                ? ` → ${daytimeToString(daytime[1])}`
                : "";
            return daytimeToString(daytime[0]) + end;
          }
        )
        .otherwise(() => null);
    });
    const displayParticipant = async (id: PeopleId) => {
      const people = (await controller.people.get(id)).release();
      if (people) return people.name?.display || people.email;
    };
    const holderMeta = await match(action.value)
      .with(
        { document: Pattern.not(Pattern.nullish) },
        async ({ document: { id, section } }) => {
          const meta = await match<
            ReferenceId,
            Promise<{ text: string; icon: Component | string | null }>
          >(id satisfies ReferenceId)
            .with(
              Pattern.union(
                { kind: "organization" },
                { kind: "agent" },
                { kind: "automation" },
                { kind: "conversation" },
                { kind: "automation_run" }
              ),
              async (obj) => {
                const presentation = referencePresentation(obj.kind);
                const resolved = await resolveReferenceTitle(controller, obj);
                return {
                  text: resolved.exists
                    ? (resolved.title ?? presentation.fallbackTitle)
                    : presentation.fallbackTitle,
                  icon: presentation.icon,
                };
              }
            )
            .with({ kind: "event" }, async (obj) => {
              const event = (await controller.event.get(obj.id)).release();
              return {
                text: event?.title || "Event",
                icon: EventIcon,
              };
            })
            .with({ kind: "event-rec" }, async (obj) => {
              const event = (
                await controller.event.recurrent.get(obj.id)
              ).release();
              return {
                text: event?.title || "Event",
                icon: EventIcon,
              };
            })
            .with({ kind: "task" }, async (obj) => {
              const task = (await controller.task.get(obj.id)).release();
              return {
                text: task?.title || "Task",
                icon: TaskIcon,
              };
            })
            .with({ kind: "page" }, async (obj) => {
              const page = (await controller.page.get(obj.id)).release();
              return {
                text: page?.title || "Page",
                icon: PageIcon,
              };
            })
            .with({ kind: "people" }, async (obj) => {
              const name = await displayParticipant(obj.id);
              return {
                text: name || "Person",
                icon: PersonIcon,
              };
            })
            .with({ kind: "task-rec" }, async (obj) => {
              const task = (
                await controller.taskRecurrent.get(obj.id)
              ).release();
              return {
                text: task?.title || "Task",
                icon: TaskIcon,
              };
            })
            .with({ kind: "object" }, async (obj) => {
              const db = (
                await controller.graph.database.ofObject(obj.id)
              ).release();
              const typeName = db?.data_name || "Object";
              const icon = getTypeIcon(profile.value, db);

              const title = await getObjectTitle(controller, obj.id);

              return { text: title || typeName, icon };
            })
            .with({ kind: "table_ref" }, async (obj) => {
              const { workspace_id: workspaceId, table_id: tableId } =
                await controller.id.tableRef.parse(obj.id);
              const db = (
                await controller.graph.database.get(workspaceId, tableId)
              ).release();
              return {
                text: db?.data_name || "Database",
                icon: getTypeIcon(profile.value, db),
              };
            })
            .with({ kind: "workspace_ref" }, async (obj) => {
              const workspaceId = await controller.id.workspace(obj);
              const workspace = getWorkspace(
                profile.value,
                workspaceId
              )?.workspace;
              return {
                text: workspace?.name || "Workspace",
                icon: WorkspaceIcon,
              };
            })
            .exhaustive();
          return {
            text: section ? `${meta.text} / ${section[0]}` : meta.text,
            icon: meta.icon,
          };
        }
      )
      .with(
        { calendar: Pattern.not(Pattern.nullish) },
        async ({ calendar: id }) => {
          const calendar = (await controller.calendar.get(id)).release();
          if (!calendar) return null;
          return { text: calendar.summary, icon: null };
        }
      )
      .with({ type: "task" }, () => null)
      .otherwise(() => null);
    const holder = holderMeta?.text ?? null;
    const holderTypeIcon = holderMeta?.icon ?? null;
    const calendarColorBar = await match(action.value)
      .with(
        { calendar: Pattern.not(Pattern.nullish) },
        async ({ calendar: id }) => {
          const calendar = (await controller.calendar.get(id)).release();
          if (!calendar) return null;
          return calendar.color;
        }
      )
      .otherwise(() => null);
    const participants = await match(action.value)
      .with(
        { participants: Pattern.not(Pattern.nullish) },
        async ({ participants }) => {
          const promises = participants.map(async (participant) => {
            return await match(participant)
              .with({ id: Pattern.select() }, async (id) => {
                return await displayParticipant(id);
              })
              .with(Pattern.string, (email) => email)
              .exhaustive();
          });
          return Promise.all(promises).then(function (p) {
            return p.join(", ");
          });
        }
      )
      .otherwise(() => null);
    const recurrence = computed(() => {
      return match(action.value)
        .with(
          { recurrence: Pattern.not(Pattern.nullish) },
          ({ recurrence }) => recurrence
        )
        .with({ type: "event-creation" }, ({ date }) =>
          date instanceof Date ? null : date
        )
        .otherwise(() => null);
    });
    const recurrenceString = computed(() =>
      recurrence.value ? ppRecurrence(recurrence.value, true) : null
    );
    const starting = computed(() => {
      const rec = recurrence.value;
      if (rec === null) return null;
      else
        return rec.start
          ? formatDate(rec.start)
          : formatDate(nextOccurrences(1, rec, null)[0]);
    });
    const ending = computed(() => {
      const rec = recurrence.value;
      if (rec === null || !("end" in rec)) return null;
      else return rec.end ? formatDate(rec.end) : null;
    });
    const allocation = computed(() => {
      return match(action.value)
        .with({ type: "event-allocation" }, ({ daytime }) => !daytime)
        .with({ type: "task-allocation" }, ({ daytime }) => !daytime)
        .otherwise(() => false);
    });
    const nOccurrences: ComputedRef<number | null> = computed(() => {
      return match(action.value)
        .with(
          { recurrence: Pattern.not(Pattern.nullish) },
          ({ recurrence }) => {
            return occurrenceCount(recurrence);
          }
        )
        .otherwise(() => null);
    });
    // FIXME - add && !action.value.duration when the back end allows it
    const allday = computed(
      () => action.value.type === "event-creation" && !action.value.daytime
    );
    const showLabelsAndPriority = computed(() =>
      match(action.value)
        .with(
          { type: "task" },
          { type: "task-allocation" },
          { type: "task-postponing" },
          { type: "task-scheduling" },
          () => true
        )
        .otherwise(() => false)
    );
    const labelsDisplay = computed(() => {
      if (!showLabelsAndPriority.value) return null;
      if (!labels.value || labels.value.length === 0) return null;
      return labels.value.map((l) => ({
        name: l.name,
        color: resolveLabelColor(browserStore, l.id, l.name),
      }));
    });
    const priorityDisplay = computed(() => {
      if (!showLabelsAndPriority.value) return null;
      if (!priority.value) return null;
      return priority.value;
    });
    const priorityColor = computed(() => {
      if (!priority.value) return null;
      return getColor(priority.value);
    });

    const filteredContext = computed<Context | null>(() =>
      match(action.value)
        .with({ type: "custom-type" }, ({ context: withContext }) =>
          withContext ? context.value : null
        )
        .with(
          { type: "task" },
          { type: "task-allocation" },
          { type: "task-postponing" },
          { type: "task-scheduling" },
          ({ capture }) => (capture ? context.value : null)
        )
        .with(
          { type: "event-allocation" },
          { type: "event-creation" },
          { type: "page-creation" },
          { type: "page-creation" },
          { type: "task-recurrence" },
          { type: "task-move" },
          { type: "recurrent-task-move" },
          () => null
        )
        .exhaustive()
    );
    const contextIcon = computed<IconContext | null>(() =>
      getIcon(filteredContext.value)
    );
    const contextText = computed<string>(() =>
      filteredContext.value
        ? captureContextDisplayText(filteredContext.value)
        : ""
    );
    const onClick = (payload: MouseEvent) => emit("click", payload);
    const onMouseover = (payload: MouseEvent) => emit("mouseover", payload);
    // Dismiss the capture advertisement. Don't let the click reach the row
    // itself, which would submit the gated action and open the gate modal.
    const onDismiss = (payload: MouseEvent) => {
      payload.stopPropagation();
      emit("dismiss");
    };
    // Shortcut hints rendered with the same key symbols as the context menu
    // (Enter is "↵", the platform primary modifier is ⌘ on macOS and ⌃
    // elsewhere).
    const enterKeysDisplay =
      hotkeys.format.stringify(
        keymap.keyEnter,
        keymap.formatSymbol,
        keymap.separatorSpace
      ) ?? "";
    const modEnterKeysDisplay =
      hotkeys.format.stringify(
        [keymap.keyMod, keymap.keyEnter],
        keymap.formatSymbol,
        keymap.separatorSpace
      ) ?? "";
    return {
      actionText,
      allday,
      allocation,
      calendarColorBar,
      contextIcon,
      contextText,
      date,
      dateKeyword,
      daytime,
      duration,
      ending,
      filteredContext,
      gate,
      holder,
      holderTypeIcon,
      icon,
      iconProps,
      iconImage,
      labelsDisplay,
      enterKeysDisplay,
      mandateCompletionStopper,
      modEnterKeysDisplay,
      nOccurrences,
      onClick,
      onDismiss,
      onMouseover,
      participants,
      priorityColor,
      priorityDisplay,
      recurrence: recurrenceString,
      starting,
      titleDisplay,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/console.scss" as *;

.console-action {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  cursor: default;
  // Unlike $paddingCompletion (8px top / 10px bottom), the action rows use
  // symmetric vertical padding: the asymmetric variant reads bottom-heavy on
  // both the single-line actions and the two-line capture actions.
  padding: 8px 10px;
  align-items: center;
  @extend %font-caption-regular;
  @include transition(background-color 100ms ease);

  .action-description {
    display: flex;
    flex-direction: column;
    row-gap: 5px;
    // Shrinkable so the trailing submit hints keep their natural width; the
    // title span inside handles the ellipsis.
    flex: 1 1 auto;
    min-width: 0;

    .first-line,
    .second-line {
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    .first-line {
      .icon-url {
        flex-shrink: 0;
        margin-inline-end: 8px;
        width: 14px;
        height: 14px;
        border-radius: 2px;
        object-fit: contain;
      }

      :deep(.icon) {
        flex-shrink: 0;
        margin-inline-start: 0;
        margin-inline-end: 8px;
        width: 14px;
        height: 14px;

        .base-icon {
          width: 14px;
          height: 14px;
          position: relative;
        }
      }

      .action-text {
        display: inline;
        vertical-align: middle;
        flex-shrink: 0;
        @extend %font-body-regular;
        @include transition(color 100ms ease);

        &.title {
          flex-shrink: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 60%;
        }
      }

      .base-type-icon {
        :deep(.stroke) {
          @include themed() {
            stroke: t($grey-70);
          }
        }
        :deep(.fill) {
          @include themed() {
            fill: t($grey-70);
          }
        }
      }

      .text {
        margin-inline-end: 4px;
        font-weight: 500;
        @include themed() {
          color: t($grey-70);
        }
      }

      .title {
        margin-inline-end: 4px;
        font-weight: normal;
        @include themed() {
          color: t($grey-50);
        }
      }

      .action {
        font-weight: 500;
        margin-inline-end: 4px;
        @include themed() {
          color: t($grey-40);
        }
      }

      .label {
        margin-inline-end: 4px;
        font-weight: 500;
        @include themed() {
          color: t($grey-70);
        }
      }

      .info {
        margin-inline-end: 4px;
        font-weight: normal;
        @include themed() {
          color: t($grey-50);
        }
      }

      .calendar-bar {
        flex-shrink: 0;
        margin: 0 6px;
      }

      .holder-type-icon {
        flex-shrink: 0;
        margin: 0 4px 0 6px;
        height: 12px;
        width: 12px;
        @include themed() {
          color: t($grey-50);
        }
        // Ensure inline SVG icons don't inherit "Move" red.
        :deep(svg) {
          color: currentColor;
        }
      }

      .label-item {
        display: inline-flex;
        align-items: center;
      }

      .label-dot {
        flex-shrink: 0;
        height: 6px;
        width: 6px;
        border-radius: 3px;
        margin-inline-end: 3px;
        &.option-color-a {
          background: $calendar-a-50;
        }
        &.option-color-b {
          background: $calendar-b-50;
        }
        &.option-color-c {
          background: $calendar-c-50;
        }
        &.option-color-d {
          background: $calendar-d-50;
        }
        &.option-color-e {
          background: $calendar-e-50;
        }
        &.option-color-f {
          background: $calendar-f-50;
        }
        &.option-color-g {
          background: $calendar-g-50;
        }
        &.option-color-h {
          background: $calendar-h-50;
        }
        &.option-color-i {
          background: $calendar-i-50;
        }
      }

      .priority-mark {
        flex-shrink: 0;
        margin-inline-end: 2px;
        font-weight: 600;
        &.option-color-a {
          color: $calendar-a-50;
        }
        &.option-color-b {
          color: $calendar-b-50;
        }
        &.option-color-c {
          color: $calendar-c-50;
        }
        &.option-color-d {
          color: $calendar-d-50;
        }
        &.option-color-e {
          color: $calendar-e-50;
        }
        &.option-color-f {
          color: $calendar-f-50;
        }
        &.option-color-g {
          color: $calendar-g-50;
        }
        &.option-color-h {
          color: $calendar-h-50;
        }
        &.option-color-i {
          color: $calendar-i-50;
        }
      }

      .inbox,
      .batch {
        flex-shrink: 0;
        margin-inline-start: 4px;
        margin-inline-end: 8px;
        position: relative;
        top: 1px;
        max-height: 14px;
        max-width: 14px;
      }
    }

    .second-line {
      margin-inline-start: 22px;
      column-gap: 6px;
      width: calc(100% - 22px);
      min-width: 0;
      .context-icon {
        height: 12px;
        width: 12px;
      }

      .context-text {
        @extend %font-small-regular;
        // Single nowrap line: drop the extra leading of the small font so the
        // line box doesn't pad the bottom of the action row.
        line-height: 13px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        @include themed() {
          color: t($grey-50);
        }
      }
    }
  }

  &.selected .action-description .first-line {
    > .base-type-icon:first-child {
      :deep(.stroke) {
        @include themed() {
          stroke: t($secondary);
        }
      }

      :deep(.fill) {
        @include themed() {
          fill: t($secondary);
        }
      }
    }

    :deep(.badge-bg) {
      @include themed() {
        background: t($grey-10);
      }
    }

    .title {
      @include themed() {
        color: t($grey-70);
      }
    }

    .text {
      @include themed() {
        color: t($secondary);
      }
    }

    .label {
      @include themed() {
        color: t($grey-70);
      }
    }

    .action {
      @include themed() {
        color: t($grey-40);
      }
    }

    .info {
      @include themed() {
        color: t($grey-70);
      }
    }
  }
  // A gated action (not entitled, or plugin disabled) can't be performed as
  // is: grey out its content, but keep the gate icon at full strength so the
  // reason remains legible.
  &.gated .action-description {
    opacity: 0.5;
  }

  .gate {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    column-gap: 8px;
    margin-inline-start: 8px;

    // The icon's own .stroke/.fill classes provide the themed grey.
    .gate-icon {
      width: 14px;
      height: 14px;
    }

    // Dismiss cross: quieter than the gate icon at rest, regular icon grey
    // on hover.
    .gate-dismiss {
      width: 14px;
      height: 14px;

      :deep(.fill) {
        @include themed() {
          fill: t($grey-40);
        }
      }

      &:hover :deep(.fill) {
        @include themed() {
          fill: t($grey-70);
        }
      }
    }
  }

  .submit-hints {
    opacity: 0;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    column-gap: 10px;
    margin-inline-start: 8px;
    @include transition(opacity 100ms ease);
    &.selected {
      opacity: 1;
    }

    .hint {
      display: flex;
      align-items: center;
      column-gap: 3px;
      @extend %font-small-regular;
      @include themed() {
        color: t($grey-50);
      }
    }

    .keys {
      line-height: 1;
      white-space: nowrap;
    }
  }
}
</style>
